import { useCallback } from "react";
import { useChatStore } from "@/stores/chatStore";

const STREAM_INTERRUPTED = "The reply was cut off. Try again.";

// An error whose message is safe to show in the chat bubble
class ChatError extends Error {}

export function useChatStream() {
  const sendMessage = useCallback(async (text: string) => {
    const store = useChatStore.getState();

    // Add user message to store
    store.addUserMessage(text);

    // Prepare history (exclude the message we just added since we send it separately)
    const history = store.messages.map((m) => ({
      role: m.role,
      content: m.content,
    }));

    // Create empty assistant message to stream into
    store.addAssistantMessage("");
    store.setStreaming(true);

    try {
      const response = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: text,
          zone: store.currentZone,
          history,
        }),
      });

      if (!response.ok) {
        // The API returns { message } for rate limits and upstream failures
        const body = await response.json().catch(() => null);
        throw new ChatError(
          body?.message ?? "Sorry, something went wrong. Try again."
        );
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No readable stream");

      const decoder = new TextDecoder();
      // SSE events can be split across network chunks, so keep any partial
      // line around until the rest of it arrives
      let buffer = "";

      read: while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (data === "[DONE]") break read;
          try {
            const parsed = JSON.parse(data);
            if (parsed.error) throw new ChatError(STREAM_INTERRUPTED);
            if (typeof parsed.token === "string") {
              useChatStore.getState().appendToLastAssistant(parsed.token);
            }
          } catch (err) {
            if (err instanceof ChatError) throw err;
            // Ignore malformed events rather than printing raw JSON
          }
        }
      }
    } catch (error) {
      console.error("Chat stream error:", error);
      const state = useChatStore.getState();
      const msgs = state.messages;
      const last = msgs[msgs.length - 1];
      const text =
        error instanceof ChatError
          ? error.message
          : "Sorry, something went wrong. Try again.";
      if (last && last.role === "assistant") {
        // Fill the empty bubble, or note the cut-off under a partial answer
        useChatStore
          .getState()
          .appendToLastAssistant(last.content === "" ? text : `\n\n_${text}_`);
      }
    } finally {
      useChatStore.getState().setStreaming(false);
    }
  }, []);

  return { sendMessage };
}
